// @flow

import * as React from "react"
import Modal from "react-modal"
import {connect} from "react-redux"
import {withTranslation} from "react-i18next"
import {format, isFuture} from "date-fns"
import ClassNames from "classnames"

import Config from "../config"
import UpdateStateModal from "./modals/update-state"
import PlanIntakeModal from "./modals/plan-intake"
import BabyBornModal from "./modals/baby-born"
import {getBabyPartnerNamesConcat} from "../reducers/babies"
import BabyCardActions from "./baby-card-actions"
import BabyStateLabel from "./baby-state-label"
import type {Baby} from "../types"

type Props = {
  baby: Baby,
  t: any,
  isAdmin: boolean,
  onlyActions?: boolean,
  latestOfferDeposit?: string,
  currentDeposit?: string,
}

type ModalNameType = "updateState" | "planIntake" | "babyBorn"

type State = {
  +isModalOpen: boolean,
  +modalName: ?ModalNameType,
}

class BabyCard extends React.Component<Props, State> {
  static defaultProps = {
    latestOfferDeposit: "0",
    currentDeposit: "0",
  }

  state = {
    isModalOpen: false,
    modalName: null,
  }

  constructor(props) {
    super(props)
    Modal.setAppElement("#app")
  }

  handleBabyStateClick: () => void = () => {
    this.setState({isModalOpen: true, modalName: "updateState"})
  }

  handlePlanIntakeClick: () => void = () => {
    this.setState({isModalOpen: true, modalName: "planIntake"})
  }

  handleBabyBornClick: () => void = () => {
    const {baby} = this.props
    const editUrl = `${Config.baseUrl}/admin/babies/${baby.id}`
    if ((baby.sex_2 || baby.sex_3) && document.location.href != editUrl) {
      document.location.href = editUrl
    } else {
      this.setState({isModalOpen: true, modalName: "babyBorn"})
    }
  }

  handleCloseModal: () => void = () => {
    this.setState({isModalOpen: false, modalName: null})
  }

  renderUpdateStateModal() {
    const {baby, onlyActions} = this.props
    const {isModalOpen, modalName} = this.state
    if (!isModalOpen || modalName !== "updateState") return null
    return (
      <UpdateStateModal
        handleCloseModal={this.handleCloseModal}
        baby={baby}
        reload={onlyActions}
      />
    )
  }

  renderBabyBornModal() {
    const {baby, onlyActions} = this.props
    const {isModalOpen, modalName} = this.state
    if (!isModalOpen || modalName !== "babyBorn") return null
    return (
      <BabyBornModal
        handleCloseModal={this.handleCloseModal}
        baby={baby}
        reload={onlyActions}
      />
    )
  }

  renderPlanIntakeModal() {
    const {baby, onlyActions} = this.props
    const {isModalOpen, modalName} = this.state
    if (!isModalOpen || modalName !== "planIntake") return null
    return (
      <PlanIntakeModal
        handleCloseModal={this.handleCloseModal}
        baby={baby}
        reload={onlyActions}
      />
    )
  }

  renderSingleName(id, firstname, sex) {
    const {t} = this.props
    return (
      <div key={`baby-name-${id}`} className="baby-card-name">
        {(firstname || sex) && <span className="mr-2">👶</span>}
        {firstname && <span className="name">{firstname}</span>}
        {sex && (
          <span className="gender">({t(`babyCard.babyName.sex.${sex}`)})</span>
        )}
      </div>
    )
  }

  renderName() {
    const {baby} = this.props
    let babies = [this.renderSingleName(1, baby.firstname, baby.sex)]
    if (baby.sex_2 && baby.sex_2 != "") {
      babies.push(this.renderSingleName(2, baby.firstname_2, baby.sex_2))
    }
    if (baby.sex_3 && baby.sex_3 != "") {
      babies.push(this.renderSingleName(3, baby.firstname_3, baby.sex_3))
    }
    return babies
  }

  renderDates() {
    const {baby, t} = this.props

    return (
      <>
        {baby.intakeDate && isFuture(baby.intakeDate) && (
          <div className="baby-card-dates">
            <div className="baby-card-date">
              <span className="baby-card-date-label">
                {t("babyCard.dates.intake")}
              </span>
              <time className="baby-card-date-date">
                {format(baby.intakeDate, "DD/MM/YY, HH:mm")}
              </time>
              {baby.intakeLocation && baby.intakeLocation.length ? (
                <p className="small mb-1 text-muted">{baby.intakeLocation}</p>
              ) : null}
            </div>
          </div>
        )}
        <div className="baby-card-dates">
          {!baby.birthDate && (
            <div className="baby-card-date">
              <span className="baby-card-date-label">
                {t("babyCard.dates.due")}
              </span>
              <time className="baby-card-date-date">
                {format(baby.dueDate, "DD/MM/YY")}
              </time>
            </div>
          )}
          {baby.birthDate && (
            <div className="baby-card-date">
              <span className="baby-card-date-label">
                {t("babyCard.dates.birth")}
              </span>
              <time className="baby-card-date-date">
                {format(baby.birthDate, "DD/MM/YY")} 🎉
              </time>
            </div>
          )}
          {baby.pickupDate && isFuture(baby.pickupDate) ? (
            <div className="baby-card-date">
              <span className="baby-card-date-label">
                {t("babyCard.dates.pickup")}
              </span>
              <time className="baby-card-date-date">
                {format(baby.pickupDate, "DD/MM/YY")}
              </time>
            </div>
          ) : null}
        </div>
      </>
    )
  }

  renderMadam() {
    const {baby} = this.props
    return (
      <div className="baby-card-madam">
        <span>{baby.madam.name}</span>
      </div>
    )
  }

  renderHeader() {
    const {baby} = this.props
    return (
      <div className="baby-card-header">
        <h3>{getBabyPartnerNamesConcat(baby)}</h3>
        <BabyStateLabel
          state={baby.state}
          babyId={baby.id}
          onClick={this.handleBabyStateClick}
        />
      </div>
    )
  }

  renderListType() {
    const {baby} = this.props

    if (
      !["co_sleeper", "cards_sugar", "sugar", "sugar_list_too_late"].includes(
        baby.babyListType
      )
    ) {
      return
    }

    return (
      <div className="baby-card-list-type">
        <span className="badge badge-light">{baby.babyListType}</span>
      </div>
    )
  }

  render() {
    const {baby, isAdmin, onlyActions, currentDeposit, latestOfferDeposit} =
      this.props

    const actions = (
      <BabyCardActions
        baby={baby}
        onPlanIntake={this.handlePlanIntakeClick}
        onBabyBorn={this.handleBabyBornClick}
        latestOfferDeposit={latestOfferDeposit}
        currentDeposit={currentDeposit}
        isStandalone={onlyActions}
      />
    )
    const card = onlyActions ? (
      actions
    ) : (
      <>
        {this.renderHeader()}
        <div className="baby-card-row">
          {this.renderName()}
          {this.renderListType()}
          {this.renderDates()}
          {isAdmin && this.renderMadam()}
        </div>
        {actions}
      </>
    )

    const className = ClassNames("baby-card", {
      "is-standalone": onlyActions,
    })

    return (
      <>
        <div className={className}>{card}</div>
        {this.renderUpdateStateModal()}
        {this.renderPlanIntakeModal()}
        {this.renderBabyBornModal()}
      </>
    )
  }
}

export default withTranslation()(connect()(BabyCard))
